import React from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import cx from 'classnames';
import PropTypes from 'prop-types';
import { DateTime } from 'luxon';

import withStyles from '@mui/styles/withStyles';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';

import DeleteIcon from '@mui/icons-material/Delete';
import PlusIcon from '@mui/icons-material/Add';

import { updatePerson } from '../store/slices/personsSlice';
import { setIntendedReferrerCompanyId } from '../store/slices/intendedInfoSlice';
import { addOrUpdateContacts } from '../store/slices/emergencyContactsSlice';
import { clearTaskAccomplishmentInfo } from '../store/slices/dashboardStatsSlice';
import { setApiResponseInfo } from '../store/slices/apiResponseInfoSlice';

import {
  createStdRequest,
  apiFetch,
  validatePhoneNumber,
  validateAddress,
  isNonLocalNonLoggedInVisitor,
  constructPhoneRolesString,
  showAlert,
  logWithTime,
} from '../utils/utils';

import { outreachRepForDisplay, noteRepForDisplayAsActivity, sortIntoActivityGroups } from '../utils/activityProcessor';

import ReduxLoader from '../store/ReduxLoader';
import DA from '../store/ReduxAccess';
import SocketAccess from '../comm/SocketAccess';

import ActivitySummaryBox from '../components/ActivitySummaryBox';
import ActionPanel from '../components/ActionPanel';
import ClickAwayPhoneInput from '../components/ClickAwayPhoneInput';
import ActivityList from '../components/ActivityList';
import NoteAssocGroup from '../components/NoteAssocGroup';
import TaskAssocGroup from '../components/TaskAssocGroup';
import ClickAwayTextField from '../components/ClickAwayTextField';
import FacePageAddressPanel from '../components/FacePageAddressPanel';
import ConfirmModal from '../components/ConfirmModal';
import AlertModal from '../components/AlertModal';
import MergeModal from '../components/MergeModal';
import KSpinner from '../components/KSpinner';
import KSelect from '../components/KSelect';

import facePageStyles from '../assets/jss/crm/views/facePageStyles';

const referrerOrgPageStyles = (theme) => ({
  ...facePageStyles(theme),
  nameBox: {
    borderBottom: '1px solid #dce3e6',
    padding: '0 10px',
    backgroundColor: '#fff',
  },
  iconBox: {
    width: '40px',
    height: '40px',
    marginRight: '10px',
  },
  relationshipDiv: {
    minWidth: '200px',
    maxWidth: '240px',
  },
  relationshipSpan: {
    display: 'inline-block',
    overflow: 'hidden',
    maxWidth: '95%',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    verticalAlign: 'top',
  },
  firmTitleSpan: {
    display: 'inline-block',
    overflow: 'hidden',
    maxWidth: '65%',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    verticalAlign: 'top',
  },
  nameBoxDropdownsDiv: {
    display: 'flex',
    marginTop: '-6px',
  },
  nameBoxDropdownDiv: {
    width: '40%',
  },
  nameBoxDropdownSpacerDiv: {
    width: '20%',
  },
});

class EmergencyContactPage extends React.Component {
  possibleContactPhoneRoles = ['Mobile', 'Home', 'Work'];
  allPossiblePhoneRoles = ['Mobile', 'Business', 'Home', 'Work', 'Fax'];
  shouldShowMergeAndDeleteMenu = false;

  state = {
    loading: true,
    noPush: false,
    shouldShowDeletionConfirmation: false,
    shouldShowMergeModal: false,
    showNewTaskModal: false,
    shouldShowAddPhoneNumberButton: true,
    contact: {
      personId: 0,
      name: '',
      relationship: '',
      description: '',
      phoneNumber: '',
      email: '',
      address: {
        street: '',
        city: '',
        state: '',
        zip: '',
      },
      inactive: false,
    },
    activityGroups: undefined,
    nActivities: 0,
    editingPhones: [false, false, false],
    preEditPhoneRoles: ['', '', ''],
    preEditPhoneNumbers: ['', '', ''],
    editingAddress: false,
    preEditAddress: undefined,
    dummyRefreshCounter: 0,
  };

  newPhoneObject = null;

  addressValidationPolicy = {
    requireAddress: false,
    forbidPartialAddress: true,
    allowZipFreeAddress: true,
  };

  componentDidMount() {
    this.registerRefreshCallbacks();
    this.loadRequiredDataAndDisplay();
  }

  registerRefreshCallbacks() {
    const personCallbackStruct = {
      kind: 'person',
      pageName: 'EmergencyContactPage',
      refreshFunc: () => this.redisplayPageForChangedPerson(),
    };
    SocketAccess.registerRefreshCallback(personCallbackStruct);

    const outreachCallbackStruct = {
      kind: 'outreach',
      pageName: 'EmergencyContactPage',
      refreshFunc: () => this.redisplayPageForChangedOutreach(),
    };
    SocketAccess.registerRefreshCallback(outreachCallbackStruct);
  }

  redisplayPageForChangedPerson() {
    const intendedContactId = DA.getIntendedEmergencyContactId();
    this.setStateContact(intendedContactId);
  }

  redisplayPageForChangedOutreach() {
    logWithTime('In EmergencyContactPage calling buildActivityGroupsFromReduxStore() in hopes of showing new call');
    this.buildActivityGroupsFromReduxStore();
  }

  componentWillUnmount() {
    clearInterval(this.refreshDeltaUpdaterIntervalId);
    SocketAccess.unregisterRefreshCallbacksForPage('EmergencyContactPage');
  }

  loadRequiredDataAndDisplay() {
    this.setState({ loading: true, loadProgress: 0 });
    const viewingFirmId = DA.getFirmId();
    const intendedContactId = DA.getIntendedEmergencyContactId();
    const completionCallback = () => {
      this.setStateContact(intendedContactId);
      this.buildActivityGroupsFromReduxStore();
      this.setState({ loading: false, loadProgress: 0 });
    };

    ReduxLoader.loadInfoForEmergencyContact(this, viewingFirmId, intendedContactId, completionCallback);
  }

  setStateContact(contactId) {
    const contact = DA.getPersonById(contactId);
    const haveAddresses = contact.addresses && contact.addresses.length > 0;
    const address = haveAddresses
      ? {
          street: contact.addresses[0].line1,
          city: contact.addresses[0].city,
          state: contact.addresses[0].state,
          zip: contact.addresses[0].zip,
        }
      : {
          street: '',
          city: '',
          state: '',
          zip: '',
        };
    const clientId = DA.getEmergencyContactee();
    const relationship = DA.getClientContactRelationship(clientId, contactId);
    const description = DA.getClientContactDescription(clientId, contactId);
    const augmentedContact = { ...contact, name: contact.preferredFullName, address, relationship, description };
    this.setState({ contact: augmentedContact });
  }

  buildActivityGroupsFromReduxStore() {
    const intendedContactId = DA.getIntendedEmergencyContactId();
    const unresolvedOutreaches = DA.getDirectOutreachHistoryForPersonWithId(intendedContactId);
    const outreaches = unresolvedOutreaches.map((o) => DA.resolveOutreachDates(o));
    const augmentedOutreaches = outreaches.map((outreach) => outreachRepForDisplay(outreach));
    const notes = this.getNotesAboutContact();
    const augmentedNotes = notes.map((note) => noteRepForDisplayAsActivity(note));
    const outreachesAndNotes = augmentedOutreaches.concat(augmentedNotes);
    const activityGroups = sortIntoActivityGroups(outreachesAndNotes);
    this.setState({ activityGroups, nActivities: outreachesAndNotes.length });
  }

  functionToShowSubject(activity) {
    switch (activity.subjectType) {
      case 'orgContact':
        return () => showAlert(this, "You're already on the page for this contact");
      case 'org':
        return () => this.showOrgFaceSheet(activity.subjectId);
      default:
        return () => showAlert(this, "Don't know how to show subject of type: " + activity.subjectType);
    }
  }

  showOrgFaceSheet(subjectId) {
    showAlert(this, 'Should now show the face sheet for org with ID: ' + subjectId);
  }

  getNotesAboutContact() {
    return DA.getNotesOnPerson(this.state.contact);
  }

  getPossibleTaskSubjects() {
    return [this.state.contact];
  }

  getTaskSubjectCategory() {
    return 'emergencyContact';
  }

  createAugmentedContactForDisplay(contact) {
    /* has only personId and relationship */
    const person = DA.getPersonById(contact.personId);
    const initial2 = person.lastName ? person.lastName[0] : '';
    const initials = person.preferredFullName[0] + initial2;
    const havePhone = person.phoneNumbers && person.phoneNumbers[0];
    const phone = havePhone ? person.phoneNumbers[0].number : '';
    return { ...person, initials, phone, relationship: contact.relationship };
  }

  handleContactStatusChange(newValue) {
    const inactive = newValue === 'Inactive';
    if (inactive !== this.state.contact.inactive) {
      const newContactState = { ...this.state.contact, inactive };
      const saveToDb = () => {
        const viewingFirmId = DA.getFirmId();
        const visibleFirmId = DA.getIntendedReferrerCompanyId();
        this.saveInactivityToDb(viewingFirmId, visibleFirmId);
      };
      this.setState({ contact: newContactState }, saveToDb);
    }
  }

  handleActionSelection(event) {
    const choice = event.target.value;
    if (choice === 'Merge') {
      this.setState({ shouldShowMergeModal: true });
    }
    if (choice === 'Delete') {
      const q = 'Are you sure you want to delete this contact?';
      this.setState({
        shouldShowDeletionConfirmationModal: true,
        deletionConfirmationQuery: q,
        deleteFunc: () => this.doDeletion(),
      });
    }
  }

  handleContactStreetChange(newValue) {
    const newAddr = { ...this.state.contact.address, street: newValue };
    const newContactState = { ...this.state.contact, address: newAddr };
    this.setState({ contact: newContactState });
  }

  handleContactCityChange(newValue) {
    const newAddr = { ...this.state.contact.address, city: newValue };
    const newContactState = { ...this.state.contact, address: newAddr };
    this.setState({ contact: newContactState });
  }

  handleContactStateChange(newValue) {
    const newAddr = { ...this.state.contact.address, state: newValue };
    const newContactState = { ...this.state.contact, address: newAddr };
    this.setState({ contact: newContactState });
  }

  handleContactZipChange(newValue) {
    const newAddr = { ...this.state.contact.address, zip: newValue };
    const newContactState = { ...this.state.contact, address: newAddr };
    this.setState({ contact: newContactState });
  }

  getContactAsListOfActionTargets() {
    const contactAsActionTarget = this.createAugmentedContactForDisplay(this.state.contact);
    return [contactAsActionTarget];
  }

  handlePhoneRolesChange = (rolesObj, index) => {
    const newPhoneRoles = constructPhoneRolesString(rolesObj, this.allPossiblePhoneRoles);
    this.handlePhoneObjectChange(newPhoneRoles, index, 'roles');
  };

  handlePhoneNumberChange = (newNumber, index) => {
    this.handlePhoneObjectChange(newNumber, index, 'number');
  };

  handlePhoneObjectChange = (newNumberOrRole, index, prop) => {
    const copiedPhoneNumbers = [...this.state.contact.phoneNumbers];
    const copiedObject = { ...copiedPhoneNumbers[index] };
    copiedObject[prop] = newNumberOrRole;
    copiedPhoneNumbers[index] = copiedObject;
    const newContactState = { ...this.state.contact, phoneNumbers: copiedPhoneNumbers };
    this.setState({ contact: newContactState });
  };

  showBlankPhoneNumber = () => {
    const phoneApiStruct = { roles: '', countryCode: 1, number: '', temp: true };
    const phoneNumbers = this.state.contact.phoneNumbers.concat(phoneApiStruct);
    const newContactState = { ...this.state.contact, phoneNumbers: phoneNumbers };
    this.setState({ contact: newContactState, shouldShowAddPhoneNumberButton: false });
  };

  startEditingPhone = (index) => {
    if (!this.state.editingPhones[index]) {
      const modifiedEditingArray = [...this.state.editingPhones];
      modifiedEditingArray[index] = true;
      const preEditRoles = this.state.contact.phoneNumbers[index].roles;
      const modifiedRolesArray = [...this.state.preEditPhoneRoles];
      modifiedRolesArray[index] = preEditRoles;
      const preEditNumber = this.state.contact.phoneNumbers[index].number;
      const modifiedNumbersArray = [...this.state.preEditPhoneNumbers];
      modifiedNumbersArray[index] = preEditNumber;
      this.setState({
        editingPhones: modifiedEditingArray,
        preEditPhoneRoles: modifiedRolesArray,
        preEditPhoneNumbers: modifiedNumbersArray,
      });
    }
  };

  handleClickOutsidePhoneInput = (index) => {
    if (!this.state.editingPhones[index]) {
      return;
    }
    if (this.isPhoneChanged(index)) {
      if (this.validateAndSavePhoneNumber(index)) {
        const modifiedEditingArray = [...this.state.editingPhones];
        modifiedEditingArray[index] = false;
        this.setState({ editingPhones: modifiedEditingArray });
      }
    }
  };

  isPhoneChanged(index) {
    const preEditRoles = this.state.preEditPhoneRoles[index];
    const currentRole = this.state.contact.phoneNumbers[index].roles;
    if (preEditRoles !== currentRole) {
      return true;
    }
    const preEditNumber = this.state.preEditPhoneNumbers[index];
    const currentNumber = this.state.contact.phoneNumbers[index].number;
    return preEditNumber !== currentNumber;
  }

  saveNameToDb() {
    this.saveContactStateFieldToDb('name');
  }

  validateAndSavePhoneNumber(changedIndex) {
    const proposedNumber = this.state.contact.phoneNumbers[changedIndex].number;
    const proposedRole = this.state.contact.phoneNumbers[changedIndex].roles;
    if (proposedNumber !== '') {
      if (proposedRole !== '') {
        const validation = validatePhoneNumber(proposedNumber);
        if (!validation.valid) {
          showAlert(this, validation.errorMessage);
          return false;
        } else {
          if (this.state.contact.phoneNumbers[changedIndex].temp) {
            const copiedPhoneNumbers = [...this.state.contact.phoneNumbers];
            const copiedObject = { ...copiedPhoneNumbers[changedIndex] };
            copiedObject['temp'] = false;
            copiedPhoneNumbers[changedIndex] = copiedObject;
            const newContactState = { ...this.state.contact, phoneNumbers: copiedPhoneNumbers };
            this.setState({ contact: newContactState });
          }
          this.saveContactStateFieldToDb('phoneNumbers');
          return true;
        }
      } else {
        showAlert(
          this,
          `Since you supplied a number for Phone ${changedIndex + 1}, you must also specify at least one role`
        );
        return false;
      }
    } else {
      showAlert(this, `Phone ${changedIndex + 1} cannot be empty`);
      return false;
    }
  }

  contactHasPhone() {
    const contact = DA.getPersonById(this.state.contact.personId);
    return contact.phoneNumbers && contact.phoneNumbers[0];
  }

  handleClickOutsideEmail(newValue) {
    console.log(`Just got handleClickOutsideEmail(${newValue})`);
    const trimmedValue = newValue.trim();
    const changed = trimmedValue !== this.state.contact.email;
    if (changed) {
      const newContactState = { ...this.state.contact, email: trimmedValue };
      this.setState({ contact: newContactState }, () => this.saveEmailToDb());
    }
  }

  saveEmailToDb() {
    this.saveContactStateFieldToDb('email');
  }

  handleClickOutsideRelationship(newValue) {
    const changed = newValue !== this.state.contact.relationship;
    if (changed) {
      const newContactState = { ...this.state.contact, relationship: newValue };
      this.setState({ contact: newContactState }, () => this.saveContactualInfoToDb('relationship'));
    }
  }

  handleClickOutsideDescription(newValue) {
    const changed = newValue !== this.state.contact.description;
    if (changed) {
      const newContactState = { ...this.state.contact, description: newValue };
      this.setState({ contact: newContactState }, () => this.saveContactualInfoToDb('description'));
    }
  }

  saveInactivityToDb() {
    this.saveContactualInfoToDb('inactive');
  }

  handlePossibleAddressChange(changed) {
    if (changed) {
      this.saveAddressToDb();
    }
  }

  saveAddressToDb() {
    const { street, city, state, zip } = this.state.contact.address;
    const { requireAddress, forbidPartialAddress, allowZipFreeAddress } = this.addressValidationPolicy;
    const validation = validateAddress(
      street,
      '',
      city,
      state,
      zip,
      requireAddress,
      forbidPartialAddress,
      allowZipFreeAddress
    );
    if (validation.valid) {
      this.saveContactStateFieldToDb('address');
    } else {
      showAlert(this, validation.errorMessage);
    }
  }

  saveContactStateFieldToDb(contactStateFieldName) {
    const contactId = this.state.contact.personId;
    const contactApiStruct = this.buildContactApiStructForUpdatingContact(contactId);
    const uri = `/api/person/${contactId}`;
    const request = createStdRequest(uri, 'put', contactApiStruct);

    const actionOnGoodResponse = (json) => {
      // store revised state in Redux
      // MKM probably need to convert to Redux form first, or maybe just use the following shortcut:
      const reduxRep = DA.getPersonById(this.state.contact.personId);
      const reduxFieldNames = this.reduxFieldNamesForContactStateName(contactStateFieldName);
      let copiedRep = reduxRep;
      reduxFieldNames.forEach(
        (reduxFieldName) => (copiedRep = { ...copiedRep, [reduxFieldName]: contactApiStruct[reduxFieldName] })
      );

      this.props.updatePerson(copiedRep);
      this.setStateContact(this.state.contact.personId);
      if (
        this.state.contact.phoneNumbers.length !== 0 &&
        !this.state.contact.phoneNumbers[this.state.contact.phoneNumbers.length - 1].temp
      ) {
        this.setState({ shouldShowAddPhoneNumberButton: true });
      }
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  saveContactualInfoToDb(infoType) {
    const clientId = DA.getEmergencyContactee();
    const contactApiStruct = this.buildContactApiStructForUpdatingContactualInfo(clientId);
    const contactId = contactApiStruct.contactId;
    const uri = `/api/person/${clientId}/contact/${contactId}`;
    const request = createStdRequest(uri, 'put', contactApiStruct);

    const actionOnGoodResponse = (json) => {
      const reduxStruct = ReduxLoader.convertContactsApiStructIntoReduxStruct([contactApiStruct]);
      this.props.addOrUpdateContacts({ clientId: clientId, contacts: reduxStruct });

      if (infoType === 'inactive') {
        const reduxRep = DA.getPersonById(contactId);
        const copiedRep = { ...reduxRep, inactive: contactApiStruct.inactive };
        this.props.updatePerson(copiedRep);
      }

      this.setStateContact(contactId);
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  deletePhoneNumber = (index) => {
    const phoneNumbers = [...this.state.contact.phoneNumbers];
    phoneNumbers.splice(index, 1);
    const newContactState = { ...this.state.contact, phoneNumbers };
    this.setState({ contact: newContactState }, () => {
      this.saveContactStateFieldToDb('phoneNumbers');
      this.clearPhoneEditingState();
    });
  };

  clearPhoneEditingState = () => {
    this.setState({
      editingPhones: [false, false, false],
      preEditPhoneRoles: ['', '', ''],
      preEditPhoneNumbers: ['', '', ''],
    });
  };

  reduxFieldNamesForContactStateName(contactStateFieldName) {
    switch (contactStateFieldName) {
      case 'phone':
      case 'phoneNumber':
        return ['phoneNumbers'];
      case 'address':
        return ['addresses'];
      case 'name':
        return ['firstName', 'preferredName', 'preferredFullName'];
      default:
        return [contactStateFieldName];
    }
  }

  handleClickOutsideName(newValue) {
    const changed = newValue !== this.state.contact.name;
    if (changed) {
      const newContactState = { ...this.state.contact, name: newValue };
      this.setState({ contact: newContactState }, () => this.saveNameToDb());
    }
  }

  buildContactApiStructForUpdatingContact(clientId) {
    /* Goal:
        public int PersonId { get; set; }
        public string Email { get; set; }
        public string FirstName { get; set; }
        public string MiddleNames { get; set; }
        public string LastName { get; set; }
        public string PreferredName { get; set; }
        public string Sex { get; set; }
        public long BirthDate { get; set; }
        // Next two are only for output
        public long LatestContactDate { get; set; }
        public string Status { get; set; }
        public string LeadTemperature { get; set; }
        public OfficialIdApiStruct OfficialId { get; set; }
        public List<PhoneApiStruct> PhoneNumbers { get; set; } = new List<PhoneApiStruct>();
        public List<AddressApiStruct> Addresses { get; set; } = new List<AddressApiStruct>();
  */
    /*
  state = {
    loading: true,
    contact: {
        personId: 0,
        name: '',
        relationship: '',
        description: '',
        phoneNumber: '',
        email: '',
        address: {
          street: '',
          city: '',
          state: '',
          zip: '',
        },
        inactive: false,
    },
  */
    const { name, personId, email, inactive } = this.state.contact;

    const trimmedName = name.trim();
    const firstSpaceLoc = trimmedName.indexOf(' ');
    const lastSpaceLoc = trimmedName.lastIndexOf(' ');
    const firstName = trimmedName.substring(0, firstSpaceLoc);
    const lastName = lastSpaceLoc >= firstSpaceLoc ? trimmedName.substring(lastSpaceLoc + 1) : '';
    const middleNames =
      lastSpaceLoc > firstSpaceLoc ? trimmedName.substring(firstSpaceLoc + 1, lastSpaceLoc).trim() : '';
    const phoneNumbers = this.state.contact.phoneNumbers.filter((p) => !p.temp);
    const addresses = this.createAddressesArrayFromState();

    return {
      personId,
      email: email.trim(),
      firstName,
      middleNames,
      lastName,
      preferredName: firstName,
      preferredFullName: firstName + ' ' + lastName,
      sex: 'Unknown',
      birthDate: null,
      phoneNumbers,
      addresses,
      inactive,
    };
  }

  buildContactApiStructForUpdatingContactualInfo(clientId) {
    /* Goal:
      public int ClientContactId { get; set; }
      public int ClientId { get; set; }
      public int ContactId { get; set; }
      public string Relationship { get; set; }
      public string Description { get; set; }
      public int ViewingFirmId { get; set; }
      public bool Inactive { get; set; }
    */
    /* From:
    state = {
      contact: {
          personId: 0,
          name: '',
          relationship: '',
          description: '',
          phoneNumber: '',
          email: '',
          address: {
            street: '',
            city: '',
            state: '',
            zip: '',
          },
          inactive: false,
      },
    */
    const { personId, relationship, description, inactive } = this.state.contact;

    return {
      clientId,
      contactId: personId,
      viewingFirmId: DA.getFirmId(),
      relationship: relationship ? relationship.trim() : '',
      description: description ? description.trim() : '',
      inactive,
    };
  }

  // createPhonesArrayFromState() {
  //   const contact = DA.getPersonById(this.state.contact.personId);

  //   if (this.newPhoneObject) {
  //     if (!this.newPhoneObject.hasOwnProperty('number')) {
  //       this.newPhoneObject = null;
  //       if (contact.phoneNumbers) {
  //         if (contact.phoneNumbers.length === 0) {
  //           return [];
  //         } else {
  //           return contact.phoneNumbers.slice(1);
  //         }
  //       } else {
  //         return [];
  //       }
  //     };
  //     const otherPhones = contact.phoneNumbers.slice(1);
  //     const phonesArray = [this.newPhoneObject].concat(otherPhones);
  //     this.newPhoneObject = null;
  //     return phonesArray;
  //   } else {
  //     return contact.phoneNumbers.slice(0);
  //   }
  // }

  createAddressesArrayFromState() {
    const stateAddress = this.state.contact.address;
    const address = {
      line1: stateAddress.street ? stateAddress.street.trim() : '',
      line2: '',
      city: stateAddress.city ? stateAddress.city.trim() : '',
      state: stateAddress.state ? stateAddress.state.trim() : '',
      zip: stateAddress.zip ? stateAddress.zip.trim() : '',
    };
    return [address];
  }

  createNoteApiStruct(noteText) {
    const now = DateTime.local();
    const authorEmail = this.props.userInfo.email;
    const viewingFirmId = DA.getFirmId();
    const referrerOrgId = this.state.contact.firmId;
    const contactId = this.state.contact.personId;
    return {
      date: now.toISO(),
      authorEmail,
      viewingFirmId,
      otherFirmId: referrerOrgId,
      subjectId: contactId,
      content: noteText,
    };
  }

  createNote(noteText) {
    const uri = '/api/note';
    const apiStruct = this.createNoteApiStruct(noteText);
    const request = createStdRequest(uri, 'POST', apiStruct);
    const actionOnGoodResponse = (json) => {
      // update Redux store
      this.props.clearTaskAccomplishmentInfo();
      ReduxLoader.addOrUpdateNotesOnPersons([json]);
      this.buildActivityGroupsFromReduxStore();
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  showUIToAddNewPhonenumber = () => {
    this.setState({ showNewTaskModal: true });
  };

  buildFirmContactApiStruct = () => {
    const { contact } = this.state;
    const { personId, email, phoneNumbers, addresses, relationship, description, inactive } = contact;
    const viewingFirmId = DA.getFirmId();
    const firstName = contact.firstName;
    const middleNames = contact.middleNames.trim();
    const lastName = contact.lastName;
    const preferredName = firstName;

    return {
      personId,
      email,
      firstName,
      middleNames,
      lastName,
      preferredName,
      phoneNumbers,
      addresses,
      firmId: this.state.contact.firmId,
      viewingFirmId,
      relationship,
      description,
      inactive,
    };
  };

  handleDeletionConfirmation() {
    this.setState({ shouldShowDeletionConfirmation: false }, () => this.doDeletion());
  }

  doDeletion() {
    const viewingFirmId = DA.getFirmId();
    const contactId = this.state.contact.personId;
    const visibleFirmId = this.state.contact.firmId;
    const uri = `/api/firm/${viewingFirmId}/visibleFirm/${visibleFirmId}/contact/${contactId}`;
    const request = createStdRequest(uri, 'DELETE');
    const actionOnGoodResponse = () => {
      // remove that contact from Redux store
      ReduxLoader.removeVisibleFirmContact(visibleFirmId, contactId);
      this.props.setIntendedReferrerCompanyId(visibleFirmId);
      this.setState({ redirect: 'referrerOrg', noPush: true });
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  handleDeletionRejection() {
    this.setState({ shouldShowDeletionConfirmation: false });
  }

  handleMergeConfirmation(targetContactId, sourceContactId) {
    this.setState({ shouldShowMergeModal: false });
    const viewingFirmId = DA.getFirmId();
    const visibleFirmId = DA.getIntendedReferrerCompanyId();
    this.retrieveMergeSourceInfoThenMerge(viewingFirmId, visibleFirmId, targetContactId, sourceContactId);
  }

  retrieveMergeSourceInfoThenMerge(viewingFirmId, visibleFirmId, targetContactId, sourceContactId) {
    const completionCallback = () => {
      this.doTheMerge(viewingFirmId, visibleFirmId, targetContactId, sourceContactId);
    };
    ReduxLoader.loadInfoForReferrerContact(this, viewingFirmId, visibleFirmId, sourceContactId, completionCallback);
  }

  doTheMerge(viewingFirmId, visibleFirmId, targetContactId, sourceContactId) {
    const targetContactName = DA.getPersonById(targetContactId).preferredFullName;
    const sourceContactName = DA.getPersonById(sourceContactId).preferredFullName;

    const uri = `/api/firm/${viewingFirmId}/visibleFirm/${visibleFirmId}/contact/${targetContactId}/merge/${sourceContactId}`;
    const request = createStdRequest(uri);
    const actionOnGoodResponse = (json) => {
      ReduxLoader.processMergeChangeInfo(json);
      this.createMergeEventThenRerender(targetContactName, sourceContactName);
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  createMergeEventThenRerender(targetContactName, sourceContactName) {
    const noteText = `Contact ${sourceContactName} was merged into ${targetContactName}`;
    this.createNote(noteText); // does just what we want; even calls createActivityGroups...() !
  }

  handleMergeCancelation() {
    this.setState({ shouldShowMergeModal: false });
  }

  rerenderPage() {
    this.buildActivityGroupsFromReduxStore();
  }

  render() {
    const overrideStyle = {
      numberInput: {
        marginTop: '0px',
      },
      labelsAndNumberDiv: {
        position: 'relative',
        left: '0px',
      },
      nativeInput: {
        width: '100%',
        fontSize: '.92rem',
      },
    };

    if (isNonLocalNonLoggedInVisitor()) {
      return <Redirect to="/homeDispatcher" />;
    }
    if (this.state.loading) {
      return <KSpinner />;
    }

    if (this.state.redirect) {
      if (this.state.noPush) {
        return <Redirect to={this.state.redirect} />;
      } else {
        return <Redirect to={this.state.redirect} push />;
      }
    }

    const { classes } = this.props;
    const allRelatedTasks = DA.getTasksWithSubject(this.state.contact.personId);
    const haveActivities =
      this.state.activityGroups && this.state.activityGroups[0] && this.state.activityGroups[0].activities[0];
    const lastInteractionTime = haveActivities ? this.state.activityGroups[0].activities[0].dateTime : 0;
    const status = this.state.contact.inactive ? 'Inactive' : 'Active';
    const phoneNumbers = this.state.contact.phoneNumbers;

    return (
      <div>
        <AlertModal show={this.state.shouldShowAlertModal} renderer={this} text={this.state.alertModalText} />

        <ConfirmModal
          show={this.state.shouldShowDeletionConfirmationModal}
          title="Confirm Deletion"
          confirmButtonLabel="Delete"
          rejectButtonLabel="Cancel"
          onConfirm={this.state.deleteFunc}
          onReject={() => this.setState({ shouldShowDeletionConfirmationModal: false })}
        >
          <div>{this.state.deletionConfirmationQuery}</div>
        </ConfirmModal>

        <MergeModal
          show={this.state.shouldShowMergeModal}
          referrerOrgId={this.state.contact.firmId}
          targetContactId={this.state.contact.personId}
          onConfirm={(targetContactId, sourceContactId) =>
            this.handleMergeConfirmation(targetContactId, sourceContactId)
          }
          onCancel={() => this.handleMergeCancelation()}
        />
        <Paper className={classes.root}>
          <div className={classes.columnHolder}>
            <div className={classes.divLeftColumn}>
              <div className={classes.nameBox}>
                <div className={classes.entityProfileFrame_titleBox}>
                  <div className={classes.iconBox}>
                    <div className={cx(classes.profileImageComponent, classes.profileImageComponent_circle)}>
                      <div className={classes.profileImageComponent_image}>
                        <img
                          src={
                            'https://ali-userassets-production.s3.amazonaws.com/public/company_logos/c/l/e/cleanrecoverycenters.com/logo.png'
                          }
                          alt="Logo"
                        />
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className={cx(classes.entityProfileFrame_title, classes.u_ellipsis)}>
                      {this.state.contact.name}
                    </div>
                    <div
                      className={cx(
                        classes.entityProfileFrame_subtitle,
                        classes.relationshipDiv,
                        classes.u_ellipsis,
                        classes.ac_h5
                      )}
                    >
                      <span className={classes.relationshipSpan}>{this.state.contact.relationship}</span>
                    </div>
                  </div>
                </div>
                <div className={classes.nameBoxDropdownsDiv}>
                  <div className={classes.nameBoxDropdownDiv}>
                    <KSelect
                      id="statusSelector"
                      fullWidth={true}
                      liveOptions={['Active', 'Inactive']}
                      value={status}
                      onChange={(event) => this.handleContactStatusChange(event.target.value)}
                    />
                  </div>
                  <div className={classes.nameBoxDropdownSpacerDiv}>&nbsp;</div>
                  {this.shouldShowMergeAndDeleteMenu && (
                    <div className={classes.nameBoxDropdownDiv}>
                      <KSelect
                        id="actionSelect"
                        fullWidth={true}
                        liveOptions={['Action', 'Merge', 'Delete']}
                        value={'Action'}
                        onChange={(event) => this.handleActionSelection(event)}
                      />
                    </div>
                  )}
                </div>
              </div>
              <div className={classes.entityProfileFrame_contentBox} style={{ backgroundColor: '#fff' }}>
                <div className={cx(classes.entityDetails, classes.js_scrollable)}>
                  <div className={cx(classes.formFramework_container, classes.formFramework_profileContainer)}>
                    <div className={cx(classes.formFramework_layout, classes.formFramework_listLayout)}>
                      <div className={classes.formFramework_listRow}>
                        <div
                          className={cx(classes.formFramework_wrapper, classes.formFramework_editWrapper, 'isRequired')}
                        >
                          <ClickAwayTextField
                            label="Name"
                            initialText={this.state.contact.name}
                            required
                            outsideClickHandler={(newValue) => this.handleClickOutsideName(newValue)}
                          />
                        </div>
                      </div>

                      {phoneNumbers.length !== 0
                        ? phoneNumbers.map((phone, index) => (
                            <div key={index}>
                              <div
                                className={classes.entitySummary}
                                style={{
                                  position: 'absolute',
                                  right: '0',
                                }}
                              >
                                {!phone.temp ? (
                                  <div
                                    className={classes.entitySummary_buttonContainer}
                                    style={{
                                      display: 'block',
                                      zIndex: '2',
                                    }}
                                  >
                                    <Tooltip title="Delete" placement="top">
                                      <IconButton
                                        aria-label="Delete"
                                        color="inherit"
                                        onClick={() => this.deletePhoneNumber(index)}
                                        size="large"
                                      >
                                        <DeleteIcon className={classes.icon_darkenOnHover} />
                                      </IconButton>
                                    </Tooltip>
                                  </div>
                                ) : (
                                  ''
                                )}
                              </div>
                              <div className={classes.formFramework_listRow}>
                                <div className={cx(classes.formFramework_wrapper, classes.formFramework_editWrapper)}>
                                  <ClickAwayPhoneInput
                                    styles={overrideStyle}
                                    possibleRoles={this.possibleContactPhoneRoles}
                                    actualRoles={phone.roles}
                                    number={phone.number}
                                    onRolesChange={(rolesObj, index) => this.handlePhoneRolesChange(rolesObj, index)}
                                    onNumberChange={(newNumber, index) =>
                                      this.handlePhoneNumberChange(newNumber, index)
                                    }
                                    onMouseDownInEditableArea={(index) => this.startEditingPhone(index)}
                                    outsidePhoneClickHandler={(index) => this.handleClickOutsidePhoneInput(index)}
                                    index={index}
                                  />
                                </div>
                              </div>
                            </div>
                          ))
                        : this.showBlankPhoneNumber()}

                      {phoneNumbers.length < 3 && this.state.shouldShowAddPhoneNumberButton && (
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          startIcon={<PlusIcon />}
                          onClick={this.showBlankPhoneNumber}
                          style={{ transform: 'translate(34px, -12px)' }}
                        >
                          Add Number
                        </Button>
                      )}

                      <div className={classes.formFramework_listRow}>
                        <div className={cx(classes.formFramework_wrapper, classes.formFramework_editWrapper)}>
                          <ClickAwayTextField
                            label="Email"
                            initialText={this.state.contact.email}
                            spaceFree
                            outsideClickHandler={(newValue) => this.handleClickOutsideEmail(newValue)}
                          />
                        </div>
                      </div>

                      <div className={classes.formFramework_listRow}>
                        <div className={cx(classes.formFramework_wrapper, classes.formFramework_editWrapper)}>
                          <ClickAwayTextField
                            label="Relationship"
                            initialText={this.state.contact.relationship}
                            multiline
                            outsideClickHandler={(changed) => this.handleClickOutsideRelationship(changed)}
                          />
                        </div>
                      </div>

                      <div className={classes.formFramework_listRow}>
                        <div className={cx(classes.formFramework_wrapper, classes.formFramework_editWrapper)}>
                          <ClickAwayTextField
                            label="Description"
                            initialText={this.state.contact.description}
                            multiline
                            outsideClickHandler={(newValue) => this.handleClickOutsideDescription(newValue)}
                          />
                        </div>
                      </div>

                      <div className={classes.formFramework_listRow}>
                        <div className={cx(classes.formFramework_wrapper, classes.formFramework_editWrapper)}>
                          <div className={classes.formFramework_label}>Address</div>
                          <div className={classes.formFramework_content}>
                            <FacePageAddressPanel
                              onClickAway={(changed) => this.handlePossibleAddressChange(changed)}
                              address={this.state.contact.address}
                              onStreetChange={(newValue) => this.handleContactStreetChange(newValue)}
                              onCityChange={(newValue) => this.handleContactCityChange(newValue)}
                              onStateChange={(newValue) => this.handleContactStateChange(newValue)}
                              onZipChange={(newValue) => this.handleContactZipChange(newValue)}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className={classes.divRightColumn}>
              <div className={classes.sideColumnStructure}>
                <div className={classes.divMysteryDiv}>
                  {false && <NoteAssocGroup notes={this.getNotesAboutContact()} />}
                  <TaskAssocGroup
                    initialSubjectId={this.state.contact.personId}
                    taskSubjectProvider={this}
                    allRelevantTasks={allRelatedTasks}
                    refreshFunc={() => this.setState({ loading: false })}
                  />
                </div>
              </div>
            </div>
            <div className={classes.divCenterColumn}>
              <div className={classes.activityColumn}>
                <ActivitySummaryBox nInteractions={this.state.nActivities} lastInteractionTime={lastInteractionTime} />
                <ActionPanel
                  page={this}
                  contacts={this.getContactAsListOfActionTargets()}
                  noteCreationFunc={(noteText) => this.createNote(noteText)}
                />
                <ActivityList
                  activityGroups={this.state.activityGroups}
                  functionToShowSubject={(activity) => this.functionToShowSubject(activity)}
                  rerenderPageFunc={() => this.rerenderPage()}
                  page={this}
                />
              </div>
            </div>
          </div>
        </Paper>
      </div>
    );
  }
}

EmergencyContactPage.propTypes = {
  classes: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  userInfo: state.userInfo,
  firms: state.firms,
});
const actionProducers = {
  setApiResponseInfo,
  updatePerson,
  setIntendedReferrerCompanyId,
  addOrUpdateContacts,
  clearTaskAccomplishmentInfo,
};

export default connect(mapStateToProps, actionProducers)(withStyles(referrerOrgPageStyles)(EmergencyContactPage));
